import Link from "next/link";
import { cn, withUrl } from "@/lib/utils";
import PrimaryButton from "@/components/ui/primary-button";
import LINKS from "@/lib/links";

import Image, { StaticImageData } from "next/image";
import GreatPlaceToWork from "@/assets/images/home/careers/great-place-to-work.png";
import BestOfHomeCareEmployerOfChoice from "@/assets/images/home/careers/best-of-home-care-employer-of-choice.png";
import GrandmotherSmiling from "@/assets/images/home/careers/caregiver-grandmother-smiling.jpeg";
import GrandfatherPose from "@/assets/images/home/careers/caregiver-grandfather-pose.jpeg";
import GrandmotherMedicine from "@/assets/images/home/careers/caregiver-grandmother-medicine.jpeg";

export type Career = {
  title?: string;
  description?: string;
};

export interface BuildRewardingCareerProps {
  id?: string;
  className?: string;
  heading?: string;
  subheading?: string;
  overrideLogos?: boolean;
  logos?: { src: string; alt: string }[];
  overrideImages?: boolean;
  images?: { src: string; alt: string }[];
  primaryCta?: {
    href: string;
    text: string;
  };
}

const careersLogos = [
  {
    src: GreatPlaceToWork,
    alt: "Great Place to Work - 2022",
  },
  {
    src: BestOfHomeCareEmployerOfChoice,
    alt: "Best of Home Care - Employer of Choice - 2024",
  },
];

const careersImages = [
  {
    src: GrandmotherSmiling,
    alt: "Caregiver and grandmother smiling at each other",
  },
  {
    src: GrandfatherPose,
    alt: "Caregiver and grandfather strike a pose for the camera",
  },
  {
    src: GrandmotherMedicine,
    alt: "Caregiver discusses medications to grandmother",
  },
];

const defaultProps: BuildRewardingCareerProps = {
  heading: "Build a Rewarding Career",
  subheading:
    "Join one of Bay Area's 50 Best Places to work in aging services.",
  primaryCta: {
    href: withUrl(LINKS.careers.href),
    text: "Search Jobs",
  },
};

const BuildRewardingCareer: React.FC<BuildRewardingCareerProps> = ({
  id = "build-rewarding-career",
  className,
  heading,
  overrideLogos,
  logos,
  overrideImages,
  images,
  subheading,
  primaryCta,
}) => {
  heading = heading ?? defaultProps.heading;
  subheading = subheading ?? defaultProps.subheading;
  primaryCta = primaryCta ?? defaultProps.primaryCta;
  const logoList = !overrideLogos ? careersLogos : (logos ?? []);
  const imageList: {
    src: string | StaticImageData;
    alt: string;
  }[] = !overrideImages ? careersImages : (images ?? []);

  return (
    <section
      id={id}
      aria-label={id}
      className={cn(
        "mx-auto flex w-full max-w-lg flex-col gap-4 p-4 md:gap-8 md:px-6 md:py-8 lg:gap-10 lg:px-3 lg:py-10",
        className
      )}
    >
      <div className="grid grid-cols-[1fr_minmax(min-content,max-content)] grid-rows-[minmax(min-content,max-content)_minmax(min-content,max-content)] gap-6 md:gap-4 lg:gap-8">
        <div className="col-span-2 md:col-span-1 md:space-y-2">
          <h2 className="font-montserrat text-heading-4 text-neutral-600 md:text-heading-3 lg:text-heading-2">
            {heading}
          </h2>
          <p className="font-montserrat text-subheading-3-400 text-neutral-500 lg:text-subheading-2">
            {subheading}
          </p>
        </div>
        {primaryCta && (
          <PrimaryButton
            id="primary-cta-home-careers"
            color="orange"
            size="md"
            className="w-fit"
            asChild
          >
            <Link
              href={primaryCta.href}
              prefetch
            >
              {primaryCta.text}
            </Link>
          </PrimaryButton>
        )}
        <div className="flex h-full flex-row items-center gap-4 md:col-start-2 md:row-span-full lg:gap-20">
          {logoList.map(({ src, alt }) => (
            <div
              key={"career-logo-" + alt}
              className="relative size-12 md:size-18 lg:size-32"
            >
              <Image
                src={src}
                alt={alt}
                fill
                className="object-contain"
              />
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-10">
        {imageList.map(({ src, alt }, index) => (
          <div
            className={cn(
              "relative max-h-[180px] min-h-[180px] w-full rounded-3xl shadow-[8px_8px_0px_0px_#FFD5BF] lg:aspect-[392/240] lg:max-h-[240px] lg:min-h-[240px] lg:max-w-[392px]",
              index === 0 && "block",
              index === 1 && "hidden md:block",
              index === 2 && "hidden lg:block"
            )}
            key={"career-image-" + alt}
          >
            <Image
              className="rounded-3xl object-cover"
              src={src}
              alt={alt}
              fill
            />
          </div>
        ))}
      </div>
    </section>
  );
};

export default BuildRewardingCareer;
